import { memo, ReactElement } from 'react';
import { isEmpty } from 'lodash-es';
import {
  Flex,
  ProgressCircle,
  Stack,
  Table as ChakraTable,
} from '@chakra-ui/react';
import { flexRender } from '@tanstack/react-table';

import { MetaPagination } from '../MetaPagination';
import { TableProps } from './types/TableProps';
import { useTable } from './hooks';
import { TableSortIcon } from './TableSortIcon';

const TableComponent = <D extends Record<string, unknown>>({
  columns,
  data,
  meta,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  getRowProps,
  isLoading = false,
  loadingElement,
  manualPagination,
  manualSorting,
  variant,
}: TableProps<D>): ReactElement => {
  const { getHeaderGroups, getRowModel, numberOfCols, setPage } = useTable({
    columns,
    data,
    manualPagination,
    manualSorting,
    meta,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
  });

  return (
    <Stack flexGrow={1} gap={4}>
      <ChakraTable.Root variant={variant}>
        <ChakraTable.Header>
          {getHeaderGroups().map((headerGroup) => (
            <ChakraTable.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <ChakraTable.ColumnHeader
                  key={header.id}
                  cursor={header.column.getCanSort() ? 'pointer' : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Flex alignItems="center" gap={4}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <TableSortIcon
                        direction={header.column.getIsSorted()}
                        isActive={!!header.column.getIsSorted()}
                      />
                    )}
                  </Flex>
                </ChakraTable.ColumnHeader>
              ))}
            </ChakraTable.Row>
          ))}
        </ChakraTable.Header>

        <ChakraTable.Body>
          {isLoading && (
            <ChakraTable.Row>
              <ChakraTable.Cell colSpan={numberOfCols}>
                {loadingElement || (
                  <ProgressCircle.Root>
                    <ProgressCircle.Circle>
                      <ProgressCircle.Track />
                      <ProgressCircle.Range />
                    </ProgressCircle.Circle>
                  </ProgressCircle.Root>
                )}
              </ChakraTable.Cell>
            </ChakraTable.Row>
          )}
          {!isLoading &&
            !isEmpty(data) &&
            getRowModel().rows.map((row) => (
              <ChakraTable.Row {...getRowProps?.(row)} key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <ChakraTable.Cell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </ChakraTable.Cell>
                ))}
              </ChakraTable.Row>
            ))}
          {!isLoading && isEmpty(data) && (
            <ChakraTable.Row>
              <ChakraTable.Cell colSpan={numberOfCols}>
                <Flex justifyContent="center">No records</Flex>
              </ChakraTable.Cell>
            </ChakraTable.Row>
          )}
        </ChakraTable.Body>
      </ChakraTable.Root>

      {meta && pagination && (
        <MetaPagination meta={meta} page={pagination.page} setPage={setPage} />
      )}
    </Stack>
  );
};

export const Table = memo(TableComponent);
